import { useCallback, useMemo, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import BootstrapTable from 'react-bootstrap-table-next';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationListStandalone,
    PaginationProvider,
    PaginationTotalStandalone,
    SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row,
    Spinner,
    UncontrolledDropdown,
} from 'reactstrap';

import Navigator from 'src/components/Navigator';
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import DateUtils from 'src/services/utils/DateUtils';
import { ROUTE_PATH_ADMIN_GUARD_FREIGHT_DETAILS } from 'src/modules/3rd-party-management/constants';
import { getCountryFlag } from '../constants';
import FilteringComponent from './components/FilteringComponent';
import SortingComponent from './components/SortingComponent';
import SummaryWidgets from './components/SummaryWidgets';
import Tabs from './components/Tabs';
import VerificationStatus from './components/VerificationStatus';
import { CargoTypes, FreightStatuses, FreightTabs } from './constants';
import useCarrierGuardFreights from './hooks/useCarrierGuardFreights';

const RoutePoint = ({
    point,
}) => (
    <span className="d-flex align-items-center gap-1">
        <img
            alt={point.country}
            className="country-flag"
            src={getCountryFlag(point.country)}
        />
        {`${point.city}, ${point.country}`}
    </span>
);

const GuardFreightsList = ({
    t,
}) => {
    const history = useHistory();
    const [activeTab, setActiveTab] = useState('all');
    const [filters, setFilters] = useState({
        search: undefined,
        status: undefined,
        createdFrom: undefined,
        createdTo: undefined,
        sortBy: 'newest',
        pageIndex: 1,
        pageSize: 10,
    });

    const dateUtils = useMemo(() => new DateUtils(), []);

    const openFreightDetails = useCallback((freightId) => {
        history.push(
            ROUTE_PATH_ADMIN_GUARD_FREIGHT_DETAILS.replace(':freightId', freightId)
        );
    }, [history]);

    const activeTabStatus = FreightTabs.find((tab) => tab.key === activeTab)?.status;

    const {
        data,
        isError,
        isFetched,
        isFetching,
    } = useCarrierGuardFreights({
        filters: {
            ...filters,
            status: activeTabStatus || filters.status,
        },
        t,
    });

    const tableColumns = useMemo(() => [
        {
            dataField: 'reference',
            text: t('Freight ID'),
            sort: false,
            style: {
                width: '170px',
            },
            formatter: (reference, row) => {
                const statusInfos = FreightStatuses[row.status];

                return (
                    <div>
                        <b className="d-block mb-1">{reference}</b>

                        <div className={`badge p-2 ${statusInfos.className}`}>
                            <i className={`${statusInfos.icon} align-middle me-1`} />
                            {t(statusInfos.title)}
                        </div>
                    </div>
                );
            },
        },
        {
            dataField: 'origin',
            text: t('Route'),
            sort: false,
            style: {
                width: '280px',
            },
            formatter: (origin, row) => (
                <div className="d-flex align-items-center gap-2">
                    <RoutePoint point={origin} />

                    <i className="ri-arrow-right-line text-muted" />

                    <RoutePoint point={row.destination} />
                </div>
            ),
        },
        {
            dataField: 'cargo',
            text: t('Cargo Details'),
            sort: false,
            style: {
                width: '200px',
            },
            formatter: (cargo) => (
                <div className="d-flex align-items-center gap-2">
                    <i className={`${CargoTypes[cargo.type]} font-size-20 text-primary`} />

                    <div>
                        <b className="d-block">{t(cargo.type)}</b>
                        <small className="text-muted">
                            {`${cargo.weight.toLocaleString('en-US')} ${t('kg')} · ${cargo.loadType}`}
                        </small>
                    </div>
                </div>
            ),
        },
        {
            dataField: 'bids',
            text: t('Bids'),
            sort: false,
            style: {
                width: '80px',
            },
        },
        {
            dataField: 'verification',
            text: t('Verification Status'),
            sort: false,
            style: {
                width: '200px',
            },
            formatter: (verification) => (
                <VerificationStatus verification={verification} />
            ),
        },
        {
            dataField: 'createdAt',
            text: t('Created'),
            sort: false,
            style: {
                width: '140px',
            },
            formatter: (createdAt) => (
                <div>
                    <span className="d-block">
                        {dateUtils.convertDateToDate(createdAt, INTERNATIONAL_DATE_FORMAT)}
                    </span>

                    <small className="text-muted">
                        {moment(createdAt).fromNow()}
                    </small>
                </div>
            ),
        },
        {
            dataField: 'actions',
            text: t('Actions'),
            sort: false,
            style: {
                width: '140px',
            },
            formatter: (_cell, row) => (
                <div className="d-flex align-items-center">
                    <Button
                        color="primary"
                        onClick={() => openFreightDetails(row.id)}
                        outline
                        size="sm"
                        type="button"
                    >
                        {t('View')}
                    </Button>

                    <UncontrolledDropdown>
                        <DropdownToggle
                            aria-label={t('Freight actions')}
                            className="border-0"
                            color="primary"
                            outline
                        >
                            <i className="ri-more-2-fill" />
                        </DropdownToggle>

                        <DropdownMenu right>
                            <DropdownItem onClick={() => openFreightDetails(row.id)}>
                                <i className="ri-eye-line me-2" />
                                {t('View Freight')}
                            </DropdownItem>
                            <DropdownItem>
                                <i className="ri-group-line me-2" />
                                {t('Review Bids')}
                            </DropdownItem>
                            <DropdownItem divider />
                            <DropdownItem>
                                <i className="ri-file-copy-line me-2" />
                                {t('Duplicate Freight')}
                            </DropdownItem>
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </div>
            ),
        },
    ], [
        dateUtils,
        openFreightDetails,
        t
    ]);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            ...newFilters,
            pageIndex: 1,
        }));
    }, []);

    const handleSortChange = useCallback((sortBy) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            sortBy,
            pageIndex: 1,
        }));
    }, []);

    const handleTabChange = useCallback((tabKey) => {
        setActiveTab(tabKey);

        setFilters((currentFilters) => ({
            ...currentFilters,
            pageIndex: 1,
        }));
    }, []);

    const handleTableChange = (type, {
        page,
        sizePerPage,
    }) => {
        if (type !== 'pagination') return;

        setFilters((currentFilters) => ({
            ...currentFilters,
            pageIndex: page,
            pageSize: sizePerPage,
        }));
    };

    const NoDataIndication = () => {
        if (isError) {
            return (
                <div className="alert alert-danger m-0" role="alert">
                    <p className="text-center mb-0">
                        {t('Unable to load freights. Please try again.')}
                    </p>
                </div>
            );
        }

        if (isFetched && !data?.freights?.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p className="text-center mb-0">{t(EMPTY_LIST)}</p>
                </div>
            );
        }

        return <></>;
    };

    const totalItemsCount = parseInt(data?.itemsCount || 0);

    return (
        <div className="page-content carrier-guard-page carrier-guard-freights-page">
            <Container fluid>
                <Row className="align-items-start mb-4">
                    <Col md="7">
                        <Navigator
                            pageTitle={t('Freights')}
                            translate={false}
                        />

                        <p className="text-muted mb-0">
                            {t('Manage your freight shipments and review carrier bids with AI-powered verification.')}
                        </p>
                    </Col>

                    <Col className="d-flex justify-content-md-end mt-3 mt-md-0" md="5">
                        <Button color="primary" type="button">
                            <i className="ri-add-line align-middle me-1" />
                            {t('Create New Freight')}
                        </Button>
                    </Col>
                </Row>

                <SummaryWidgets
                    widgets={data?.summary || []}
                />

                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>
                                <Row className="align-items-center mb-3">
                                    <Col lg="5">
                                        <Tabs
                                            activeTab={activeTab}
                                            counts={data?.tabsCounts || {}}
                                            onChange={handleTabChange}
                                        />
                                    </Col>

                                    <Col lg="7">
                                        <div className="d-flex flex-wrap justify-content-lg-end align-items-center gap-2 mt-3 mt-lg-0">
                                            <FilteringComponent
                                                onChange={handleFilterChange}
                                            />

                                            <SortingComponent
                                                onChange={handleSortChange}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <PaginationProvider
                                    pagination={paginationFactory({
                                        custom: true,
                                        page: filters.pageIndex,
                                        sizePerPage: filters.pageSize,
                                        totalSize: totalItemsCount,
                                        withFirstAndLast: true,
                                        alwaysShowAllBtns: true,
                                        prePageText: <span><i className="ri-arrow-left-s-line" /> {t('Back')}</span>,
                                        nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line" /></span>,
                                        prePageTitle: t('Pre page'),
                                        firstPageTitle: t('First page'),
                                        lastPageTitle: t('Last page'),
                                        showTotal: false,
                                        paginationTotalRenderer: (_from, _to, size) => (
                                            <span className="react-bootstrap-table-pagination-total">
                                                {`${t('Total Items')} ${size}`}
                                            </span>
                                        ),
                                        hideSizePerPage: false,
                                        sizePerPageList: [
                                            { text: '10', value: 10 },
                                            { text: '25', value: 25 },
                                            { text: '50', value: 50 },
                                        ],
                                    })}
                                >
                                    {({
                                        paginationProps,
                                        paginationTableProps,
                                    }) => (
                                        <>
                                            <BootstrapTable
                                                bordered={false}
                                                classes="table tpdd-table"
                                                columns={tableColumns}
                                                data={data?.freights || []}
                                                headerWrapperClasses="thead-light"
                                                keyField="id"
                                                loading={isFetching}
                                                noDataIndication={() => <NoDataIndication />}
                                                onTableChange={handleTableChange}
                                                overlay={overlayFactory({
                                                    spinner: <Spinner animation="border" variant="primary" size="md" />,
                                                    text: `${t('Loading')}...`,
                                                })}
                                                remote={{
                                                    pagination: true,
                                                    filter: false,
                                                    sort: false,
                                                    cellEdit: false,
                                                    search: false,
                                                }}
                                                responsive
                                                striped
                                                wrapperClasses="table-responsive"
                                                {...paginationTableProps}
                                            />

                                            <div className="tpdd-pagination-style-1">
                                                <PaginationTotalStandalone {...paginationProps} />

                                                <div>
                                                    <PaginationListStandalone {...paginationProps} />

                                                    <SizePerPageDropdownStandalone {...paginationProps} />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </PaginationProvider>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default withNamespaces()(
    GuardFreightsList
);
